import {
  ArrowBack as ArrowBackIcon,
  Logo,
  Visibility,
  VisibilityOff,
} from '../../components/icons';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayoutSkeleton from '../../components/skeletons/AdminLayoutSkeleton';
import { getErrorMessage } from '../../utils/errorUtils';
import { BRAND, NEUTRAL } from '../../theme/adminColors';

// Styled TextField with modern design
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    border: '2px solid transparent',
    transition: 'all 0.3s ease',
    '& fieldset': {
      border: '2px solid #e2e8f0',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      backgroundColor: '#f1f5f9',
      '& fieldset': {
        borderColor: '#cbd5e1',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
      '& fieldset': {
        borderColor: BRAND.main,
        borderWidth: '2px',
      },
    },
    '&.Mui-disabled': {
      backgroundColor: '#f1f5f9',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    '&.Mui-focused': {
      color: BRAND.main,
      fontWeight: 600,
    },
  },
}));

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  // Get redirect URL from query params and any message from state
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || '/admin';
  const locationMessage = location.state?.message;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage] = useState(locationMessage);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      navigate(redirectUrl, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, redirectUrl]);

  // Add fallback timeout for auth loading
  useEffect(() => {
    const fallbackTimeout = setTimeout(() => {
      if (authLoading) {
        console.warn('Auth loading timeout - showing login form anyway');
      }
    }, 15000);

    return () => clearTimeout(fallbackTimeout);
  }, [authLoading]);

  // Show loading while checking authentication (with timeout fallback)
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setShowFallback(true);
    }, 15000);

    if (!authLoading) {
      clearTimeout(fallbackTimer);
      setShowFallback(false);
    }

    return () => clearTimeout(fallbackTimer);
  }, [authLoading]);

  // Only show skeleton if auth is loading AND user is not actively trying to log in
  if (authLoading && !showFallback && !loading) {
    return <AdminLayoutSkeleton />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await login(formData.email, formData.password);
      // Login successful - navigate to redirect URL or admin
      navigate(redirectUrl, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.userMessage || getErrorMessage(error, 'Invalid email or password. Please try again.');
      setError(errorMessage);
    } finally {
      // Always reset loading state
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: BRAND.main,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.3,
        },
      }}
    >
      {/* Back to Home Button */}
      <Box sx={{ position: 'absolute', top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 }, zIndex: 10 }}>
        <Button
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.2 },
            borderRadius: '12px',
            textTransform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              transform: 'translateX(-3px)',
            },
          }}
        >
          Back to Home
        </Button>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', pt: { xs: 10, sm: 8 }, pb: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
        <div style={{ width: '100%' }}>
          <Card
            sx={{
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              mt: { xs: 6, sm: 0 },
            }}
          >
            <Box sx={{ p: { xs: 4, sm: 6 } }}>
              {/* Logo and Brand */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <div>
                  <Logo size={72} style={{ margin: '0 auto 16px' }} />
                </div>

                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    color: NEUTRAL.text,
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: NEUTRAL.textSecondary,
                    fontSize: '1rem',
                  }}
                >
                  Sign in to manage your restaurant
                </Typography>
              </Box>

              {infoMessage && (
                <div>
                  <Alert
                    severity="info"
                    sx={{
                      mb: 3,
                      borderRadius: '12px',
                      '& .MuiAlert-icon': {
                        fontSize: 24,
                      },
                    }}
                  >
                    {infoMessage}
                  </Alert>
                </div>
              )}

              {error && (
                <div>
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: '12px',
                      '& .MuiAlert-icon': {
                        fontSize: 24,
                      },
                    }}
                  >
                    {error}
                  </Alert>
                </div>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />

                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  sx={{ mb: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    variant="body2"
                    sx={{
                      color: BRAND.main,
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background: BRAND.main,
                    color: 'white',
                    py: 1.8,
                    borderRadius: '12px',
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: BRAND.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                    },
                    '&:disabled': {
                      background: BRAND.light,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    OR
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Don't have an account?
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: BRAND.main,
                      borderWidth: 2,
                      color: BRAND.main,
                      py: 1.5,
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: BRAND.main,
                        color: 'white',
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                      },
                    }}
                  >
                    Create Account
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                px: 4,
                backgroundColor: '#f9fafb',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                © {new Date().getFullYear()} merchQRy. All rights reserved.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Link href="/terms" variant="body2" sx={{ color: NEUTRAL.textSecondary, textDecoration: 'none', '&:hover': { color: BRAND.main } }}>
                  Terms
                </Link>
                <Link href="/privacy" variant="body2" sx={{ color: NEUTRAL.textSecondary, textDecoration: 'none', '&:hover': { color: BRAND.main } }}>
                  Privacy
                </Link>
                <Link href="/help" variant="body2" sx={{ color: NEUTRAL.textSecondary, textDecoration: 'none', '&:hover': { color: BRAND.main } }}>
                  Help
                </Link>
              </Box>
            </Box>
          </Card>
        </div>
      </Container>
    </Box>
  );
};

export default LoginPage;
